import React, { useEffect, useRef, useState } from 'react';
import { VolumeX } from 'lucide-react';

// YouTube Short — https://youtube.com/shorts/2jCRsBEqI1o
const YT_ID = '2jCRsBEqI1o';

// === Singleton loader for the official YouTube IFrame Player API ===
// The API script must be loaded exactly once per page. We attach a promise
// so every BottomVideo instance shares the same `window.YT` namespace.
let ytApiPromise = null;
function loadYouTubeAPI() {
  if (typeof window === 'undefined') return Promise.reject('SSR');
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    // Reserve the global before injecting the script — YT calls this when ready.
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previous === 'function') previous();
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.async = true;
    document.head.appendChild(tag);
  });
  return ytApiPromise;
}

/**
 * BottomVideo — vertical YouTube Short.
 * Behavior:
 *   • Iframe pre-mounts 800px before the section enters the viewport so it's
 *     already buffered when the user arrives.
 *   • While the section is on-screen, the video plays. After any user gesture
 *     anywhere on the page, audio turns on while the section is visible.
 *   • As soon as the section scrolls out of view (up OR down) or the tab is
 *     hidden, the video MUTES so audio never plays in the background.
 *   • Returning to the section unmutes + resumes automatically (no rebuffer
 *     because we never pause — we only mute).
 *
 * Uses the OFFICIAL YouTube IFrame Player API for reliability:
 *   • onReady fires only after the player is fully initialized → we know
 *     it's safe to call .playVideo() / .mute() / .unMute() without commands
 *     being dropped.
 *   • Falls back gracefully (renders the poster image) if YT API fails to load.
 */
export default function BottomVideo() {
  const wrapRef = useRef(null);
  const playerHostRef = useRef(null);     // <div> that YT will replace with iframe
  const playerRef = useRef(null);         // YT.Player instance
  const isReadyRef = useRef(false);
  const isVisibleRef = useRef(false);
  const userActivatedRef = useRef(false);
  const [mounted, setMounted] = useState(false);
  // Render-state mirror of the audio so we can show a tap-to-unmute pill.
  const [isMuted, setIsMuted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  // Centralized audio/play sync.
  //   • Visible            → playVideo. Unmute only after a user gesture.
  //   • Not visible/hidden → mute (keep playing → instant resume on return).
  const syncAudio = () => {
    if (!isReadyRef.current || !playerRef.current) return;
    const p = playerRef.current;
    try {
      // Always keep playing so scroll-back is instant (no rebuffer).
      p.playVideo();
      if (isVisibleRef.current && userActivatedRef.current) {
        p.unMute();
        p.setVolume(100);
      } else {
        p.mute();
      }
      // Mirror the actual muted state into render state (with a tiny defer
      // so YT's internal state has caught up).
      setTimeout(() => {
        try { setIsMuted(!!p.isMuted()); } catch (_) {}
      }, 100);
    } catch (_) {
      // YT API can occasionally throw during quick state transitions.
    }
  };

  // Explicit user-driven unmute — bound to the on-screen "TAP FOR SOUND"
  // pill. This is the most iOS-Safari-friendly path because the click is
  // unambiguously on the player overlay.
  const enableSound = () => {
    userActivatedRef.current = true;
    if (!isReadyRef.current || !playerRef.current) return;
    try {
      playerRef.current.unMute();
      playerRef.current.setVolume(100);
      playerRef.current.playVideo();
      setIsMuted(false);
    } catch (_) {}
  };

  // === Pre-mount once the section is within 800px of the viewport ===
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setMounted(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: '800px 0px', threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // === Boot the YT IFrame Player API once mounted ===
  useEffect(() => {
    if (!mounted) return;
    let cancelled = false;
    loadYouTubeAPI()
      .then((YT) => {
        if (cancelled || !playerHostRef.current) return;
        playerRef.current = new YT.Player(playerHostRef.current, {
          videoId: YT_ID,
          // 9:16 dimensions — we override via CSS but YT needs initial values.
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 1,
            mute: 1,
            playsinline: 1,
            loop: 1,
            playlist: YT_ID,           // required for loop
            controls: 1,
            rel: 0,
            modestbranding: 1,
            iv_load_policy: 3,
            fs: 0,
            cc_load_policy: 0,
            disablekb: 1,
          },
          events: {
            onReady: (e) => {
              isReadyRef.current = true;
              try {
                e.target.mute();
                e.target.playVideo();
              } catch (_) {}
              // Apply the current visibility/activation state right away.
              syncAudio();
            },
            // If YT pauses on its own (e.g. ad / network blip), nudge it back
            // to playing whenever the section is supposed to be playing.
            onStateChange: (e) => {
              const PAUSED = 2;
              const ENDED = 0;
              if (e.data === PAUSED || e.data === ENDED) {
                if (isVisibleRef.current) {
                  try { e.target.playVideo(); } catch (_) {}
                }
              }
            },
          },
        });
      })
      .catch(() => {
        // YT API failed to load (ad-blocker, CSP, network). The poster image
        // already shown beneath remains as a graceful fallback.
      });
    return () => {
      cancelled = true;
      try {
        if (playerRef.current && playerRef.current.destroy) {
          playerRef.current.destroy();
        }
      } catch (_) {}
      isReadyRef.current = false;
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  // === Visibility observer ===
  useEffect(() => {
    if (!mounted) return;
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          // ≥25% of the section visible → "user is watching"
          const visible = e.isIntersecting && e.intersectionRatio >= 0.25;
          isVisibleRef.current = visible;
          setIsVisible(visible);
          syncAudio();
        });
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  // === First user gesture unlocks audio ===
  // Listeners auto-detach after the first event via { once: true } so
  // we don't keep three capture-phase global listeners alive for the
  // entire session once their job is done.
  useEffect(() => {
    if (!mounted) return;
    const onGesture = () => {
      userActivatedRef.current = true;
      syncAudio();
    };
    const opts = { capture: true, once: true, passive: true };
    window.addEventListener('pointerdown', onGesture, opts);
    window.addEventListener('keydown', onGesture, opts);
    window.addEventListener('touchstart', onGesture, opts);
    return () => {
      // { once: true } self-removes on fire, but we still clean up if
      // the component unmounts before any gesture happened.
      window.removeEventListener('pointerdown', onGesture, opts);
      window.removeEventListener('keydown', onGesture, opts);
      window.removeEventListener('touchstart', onGesture, opts);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  // === Tab hidden / route change → mute ===
  useEffect(() => {
    if (!mounted) return;
    const onVis = () => {
      if (document.hidden) {
        try { playerRef.current && playerRef.current.mute(); } catch (_) {}
      } else {
        syncAudio();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      try {
        if (playerRef.current) {
          playerRef.current.mute();
          playerRef.current.pauseVideo();
        }
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  return (
    <section
      id="bottom-video"
      data-testid="bottom-video-section"
      ref={wrapRef}
      className="relative py-12 sm:py-20 lg:py-28 bg-black overflow-hidden"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-red-600/10 blur-3xl rounded-full pointer-events-none" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-8 sm:mb-12 reveal">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-[2px] w-10 bg-red-600" />
            <span className="text-xs tracking-[0.4em] text-red-500 font-semibold">FITNESSPRO · GALT</span>
            <div className="h-[2px] w-10 bg-red-600" />
          </div>
          <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            KNOW PAIN <span className="text-gradient-red">·</span> KNOW GAIN
          </h2>
        </div>

        {/* Portrait container — YouTube Shorts are 9:16 */}
        <div className="relative mx-auto w-full max-w-[min(86vw,420px)]">
          <div
            className="relative aspect-[9/16] rounded-2xl overflow-hidden border border-red-600/30 shadow-2xl shadow-red-600/30 bg-black"
            style={{ contain: 'layout paint' }}
            data-testid="bottom-video-frame"
          >
            {/* Poster — shows the video's first frame instantly while YT boots.
                hqdefault.jpg always exists for any video id; maxresdefault.jpg
                only exists for sufficiently high-res uploads → using it produced
                a noisy 404 (iteration_8). */}
            <img
              src={`https://i.ytimg.com/vi/${YT_ID}/hqdefault.jpg`}
              alt=""
              aria-hidden
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />

            {/* YT API replaces this <div> in-place with an <iframe>. */}
            {mounted && (
              <div className="absolute inset-0 w-full h-full">
                <div
                  ref={playerHostRef}
                  data-testid="bottom-video-player"
                  className="w-full h-full"
                />
              </div>
            )}

            {/* === Branding masks (cover YT's avatar / channel-name overlay) === */}
            <div
              aria-hidden
              data-testid="bottom-video-top-mask"
              className="pointer-events-none absolute top-0 left-0 right-0 h-[22%] bg-black z-[2] flex items-end px-3 pb-1.5"
            >
              <div className="flex items-center gap-2 text-[9px] sm:text-[10px] tracking-[0.4em] text-red-400 font-bold">
                <span className="h-[1.5px] w-4 sm:w-6 bg-red-500" />
                FITNESSPRO · GALT
              </div>
            </div>
            <div
              aria-hidden
              data-testid="bottom-video-bottom-mask"
              className="pointer-events-none absolute bottom-0 left-0 right-0 h-[18%] bg-black z-[2]"
            />

            {/* "TAP FOR SOUND" pill — appears only when the video is visible
                AND still muted (the browser blocked our auto-unmute). The
                explicit user click on the pill bypasses iOS Safari's strict
                gesture requirement. */}
            {mounted && isVisible && isMuted && (
              <button
                type="button"
                onClick={enableSound}
                data-testid="bottom-video-unmute"
                aria-label="Enable sound"
                className="absolute z-[3] left-1/2 -translate-x-1/2 bottom-[22%] inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-red-600 hover:bg-red-500 text-white text-[10px] sm:text-[11px] tracking-[0.18em] font-bold shadow-[0_8px_24px_rgba(239,43,43,0.55)] transition active:scale-95"
              >
                <VolumeX className="w-3.5 h-3.5" />
                <span>TAP FOR SOUND</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
